import React, { FC, Suspense } from 'react';
import { find, includes, map, some } from 'lodash';
import { BlockStatusCode, CommodityStatusCode, MilestoneStatusCode, PersonaType } from '@bechtel/types';
import { Box, Text, Icon, IconSize, useSideSheetState, Button, ButtonAppearance, ButtonSize } from '@bechtel/gds';
import { WeldMilestoneProps } from './types';
import { MilestoneHours } from '~/detail/commodities/milestones';
import {
  WeldMilestoneCardStyled,
  WeldMilestoneIndicatorStyled,
  WeldMilestoneTitleStyled,
  WeldMilestoneBodyStyled,
  MilestoneActionButtonStyled,
  MilestoneActionContainerStyled,
  AttachmentsBoxStyled,
} from './styled';
import { WeldMilestoneAction } from './action';
import { WeldMilestoneMetadata } from './metadata';
import { Trans } from '@lingui/macro';
import {
  ImageViewer,
  useImageViewerState,
  ErrorBoundary,
  SuspenseLoader,
  useConfig,
  useEnvProvider,
  ProjectAppTypes,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { AssignReassignWeldSidesheet } from './assign-reassign-weld-sidesheet';
import { WeldAssign } from '~/detail/commodities/weld/assign';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';

export const WeldMilestone: FC<WeldMilestoneProps> = ({
  milestone,
  disabled,
  hidden,
  isFitupMilestone,
  hasFitupRequest,
  assignee,
  weld,
  assignReassignVisible,
  setMilestoneData,
  actionLoading,
  showItrCertDialog,
}) => {
  const wp = useWorkPackage();

  const installForeman =
    weld &&
    weld.commodityAssignees &&
    find(weld.commodityAssignees, (commodityAssignee) => {
      return commodityAssignee.personaType == PersonaType.FOREMAN;
    });
  const { assignedCrewMembers } = wp;
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });
  const status = hasOpenBlocks ? MilestoneStatusCode.BLOCKED : milestone.currentStatus.codeI18n;
  const hasAttachments = !!milestone.attachments.length;
  const isAssigned = canCreateFitupRequest ? !!assignee?.id : !!installForeman?.assignee?.id;
  const { features } = useConfig();
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const { envConfig } = useEnvProvider();
  const { visible: visibleAssignWeld, open: openAssignWeld, close: closeAssignWeld } = useSideSheetState(false);
  let isFitup: boolean = false;
  let isInstall: boolean = false;

  const fitUpMilestone = weld?.milestones.find((milestone) => {
    return milestone?.name?.toUpperCase() === WeldMilestoneType.FITUP;
  });

  if (isFitupEnabled) {
    isFitup =
      includes(
        [MilestoneStatusCode.REQUESTED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.NOT_APPLICABLE],
        milestone?.currentStatus?.codeI18n
      ) &&
      includes([CommodityStatusCode.FITUP_REQUESTED, CommodityStatusCode.SENT_BACK_TO_PIPING], weld?.status?.status);

    isInstall =
      includes(
        [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.PLANNED],
        milestone?.currentStatus?.codeI18n
      ) && includes([CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK], weld?.status?.status);
  } else {
    isInstall = includes(
      [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.PLANNED],
      milestone?.currentStatus?.codeI18n
    );
  }

  const isAssignVisible = () => {
    if (!isFitup && !isInstall) return false;
    else if ((isFitup || isInstall) && !isAssigned) return true;
    else return false;
  };

  const isReassignVisible = () => {
    if ((isFitup && envConfig.appName === ProjectAppTypes.AS) || (!isFitup && envConfig.appName === ProjectAppTypes.PF))
      return false;
    if (!isFitup && !isInstall) return false;
    else if ((isFitup || isInstall) && isAssigned) return true;
    else return false;
  };
  const isAssignWeldShow = visibleAssignWeld && (isFitupEnabled ? !canCreateFitupRequest : !isFitupEnabled);

  return (
    <WeldMilestoneCardStyled status={status} disabled={disabled} isAssigned={isAssigned} data-testid="milestone-card">
      <WeldMilestoneIndicatorStyled
        status={status}
        disabled={disabled}
        isAssigned={isAssigned}
        data-testid="milestone-indicator"
      />
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} hidden={hidden}>
        <WeldMilestoneTitleStyled status={status} disabled={disabled} isAssigned={isAssigned}>
          {milestone.name}
        </WeldMilestoneTitleStyled>
        <WeldMilestoneBodyStyled>
          <MilestoneHours
            status={status}
            hours={milestone.hours}
            isFitupMilestone={isFitupMilestone}
            hasFitupRequest={hasFitupRequest}
          />
          {!disabled && (
            <WeldMilestoneMetadata
              fitUpMilestone={fitUpMilestone}
              commodityStatus={weld?.status?.status}
              status={status}
              milestone={milestone}
              assignee={canCreateFitupRequest ? assignee : installForeman?.assignee}
            />
          )}
          {disabled && (
            <Box pt={{ sm: '550' }} pb={{ sm: '500' }} justifyContent={{ sm: 'center' }}>
              <Text size="500" weight="500">
                <Trans>Not Required</Trans>
              </Text>
            </Box>
          )}
        </WeldMilestoneBodyStyled>
        {!disabled && (
          <Box
            pb={{ sm: '400' }}
            mt={{ sm: '100' }}
            px={{ sm: '400' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'flex-end' }}
          >
            <ErrorBoundary>
              <WeldMilestoneAction
                status={status}
                milestone={milestone}
                setMilestoneData={setMilestoneData}
                isFitupMilestone={isFitupMilestone}
                weld={weld}
                isAssigned={isAssigned}
                actionLoading={actionLoading}
                showItrCertDialog={showItrCertDialog}
              />
            </ErrorBoundary>
            {assignReassignVisible && (isReassignVisible() || isAssignVisible()) && (
              <MilestoneActionContainerStyled
                isReassign={isReassignVisible() || (isAssignVisible() && Boolean(weld?.fitupRequest?.id) && isFitup)}
              >
                <MilestoneActionButtonStyled
                  status={status}
                  isAssigned={isAssigned}
                  onClick={openAssignWeld}
                  data-testid="assign-reassign-button"
                  disabled={actionLoading}
                >
                  {isAssignVisible() && (
                    <Box alignItems={{ sm: 'center' }}>
                      {weld?.fitupRequest?.id && isFitup ? (
                        <Icon icon="user-plus" size={IconSize.MEDIUM} />
                      ) : (
                        <>
                          <Icon icon="user-plus" size={IconSize.MEDIUM} />
                          <Box ml={{ sm: '300' }}>
                            <Text>
                              <Trans>Assign</Trans>
                            </Text>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  {isReassignVisible() && <Icon icon="user" size={IconSize.MEDIUM} />}
                </MilestoneActionButtonStyled>
              </MilestoneActionContainerStyled>
            )}

            {hasAttachments && (
              <AttachmentsBoxStyled>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    openImageViewer();
                  }}
                  data-testid="image-viewer-button"
                >
                  <Icon icon="paperclip" size={IconSize.MEDIUM} />
                </Button>
              </AttachmentsBoxStyled>
            )}
          </Box>
        )}
      </Box>
      {visibleImageViewer && (
        <ImageViewer
          images={map(milestone.attachments, 'url')}
          visible={visibleImageViewer}
          closable={true}
          onDismiss={closeImageViewer}
        />
      )}

      {visibleAssignWeld && isFitupEnabled && canCreateFitupRequest && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <AssignReassignWeldSidesheet
            close={closeAssignWeld}
            weld={weld}
            isReassign={isReassignVisible() || Boolean(weld.fitupRequest)}
          />
        </Suspense>
      )}

      {isAssignWeldShow && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldAssign
              close={closeAssignWeld}
              existingAssignment={canCreateFitupRequest ? assignee : installForeman?.assignee}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </WeldMilestoneCardStyled>
  );
};
